import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

import { environment } from '../../environments/environment';

import { HttpErrorHandler, HandleError } from './http-error-handler.service';

import { LoanDetailOptionsListItem, PaymentOptionsListItem, StatesListItem, CountryListItem, VerifyListItem,
         CardExpireMonthYearListItem, PayoffAmount } from '../models/reference';

@Injectable({
  providedIn: 'root'
})
export class ReferenceService {
  private _logging = environment.logging;
  private _apiSettings = environment.restAPI;
  private _baseServiceUrl = '';
  private _handleError: HandleError;

  constructor(@Inject('BASE_URL') baseUrl: string, private _httpClient: HttpClient, httpErrorHandler: HttpErrorHandler) {
    this._baseServiceUrl = (this._apiSettings.useStaticUrl ? this._apiSettings.staticUrl : baseUrl) + this._apiSettings.urlSuffix;
    this._handleError = httpErrorHandler.createHandleError('ReferenceService');
  }

  public getLoanDetailOptionsList(): Observable<LoanDetailOptionsListItem[]> {
    const serviceUrl: string = this._baseServiceUrl + 'Reference/LoanDetailOptions';
    if (this._logging.serviceCallTraceLog) {
      console.log('ReferenceService.getLoanDetailOptionsList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<LoanDetailOptionsListItem>>(serviceUrl, null).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getLoanDetailOptionsList', []))
    );
  }

  public getPaymentOptionsList(): Observable<PaymentOptionsListItem[]> {
    const serviceUrl: string = this._baseServiceUrl + 'Reference/PaymentOptions';
    if (this._logging.serviceCallTraceLog) {
      console.log('ReferenceService.getPaymentOptionsList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<PaymentOptionsListItem>>(serviceUrl, null).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getPaymentOptionsList', []))
    );
  }

  public getStatesList(state: string = null): Observable<StatesListItem[]> {
    const serviceUrl: string = this._baseServiceUrl + 'Reference/States';
    if (this._logging.serviceCallTraceLog) {
      console.log('ReferenceService.getStatesList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<StatesListItem>>(serviceUrl, {state: state}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getStatesList', []))
    );
  }
  // 03/16/2021 RA Get State list filtered on Country Code
  public getStatesCountryList(country: string = null): Observable<StatesListItem[]> {
    const serviceUrl: string = this._baseServiceUrl + 'Reference/States';
    if (this._logging.serviceCallTraceLog) {
      console.log('ReferenceService.getStatesCountryList :: serviceUrl = ' + serviceUrl);
    }
    // 03/18/2021 RA - Pass in 'NULL' as this will be treated as a value in the sproc to return only the states for the country provided.
    return this._httpClient.post<Array<StatesListItem>>(serviceUrl, {state: 'NULL', country: country}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getStatesCountryList', []))
    );
  }

  // 04/01/2021 RA Verify State and Country are valid pair
  public verifyStateCountry(state: string = null, country: string = null): Observable<VerifyListItem>  {
    const serviceUrl: string = this._baseServiceUrl + 'Reference/VerifyState';

    if (this._logging.serviceCallTraceLog) {
      console.log('ReferenceService.verifyStateCountry :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<VerifyListItem>(serviceUrl, {state: state, country: country}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('verifyStateCountry', null))
    );
  }

  public getCountryList(country: string = null): Observable<CountryListItem[]> {
    const serviceUrl: string = this._baseServiceUrl + 'Reference/Country';
    if (this._logging.serviceCallTraceLog) {
      console.log('ReferenceService.getCountryList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<CountryListItem>>(serviceUrl, {country: country}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getCountryList', []))
    );
  }

  public getPayoffAmnt(note_id: number, payoffAmount: number = 0): Observable<PayoffAmount> {
    const serviceUrl: string = this._baseServiceUrl + 'Reference/PayoffAmount';

    if (this._logging.serviceCallTraceLog) {
      console.log('ReferenceService.getPayoffAmount :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<PayoffAmount>(serviceUrl, {note_id: note_id}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getPayoffAmount', { note_id: note_id, payoffAmount: payoffAmount}))
    );
  }

  public getCardExpireYearList(): Observable<CardExpireMonthYearListItem[]> {
    const serviceUrl: string = this._baseServiceUrl + 'Reference/CardExpireYear';
    if (this._logging.serviceCallTraceLog) {
      console.log('ReferenceService.getCardExpireYearList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<CardExpireMonthYearListItem>>(serviceUrl, null).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getCardExpireYearList', []))
    );
  }

  public getCardExpireMonthList(): Observable<CardExpireMonthYearListItem[]> {
    const serviceUrl: string = this._baseServiceUrl + 'Reference/CardExpireMonth';
    if (this._logging.serviceCallTraceLog) {
      console.log('ReferenceService.getCardExpireMonthList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<CardExpireMonthYearListItem>>(serviceUrl, null).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getCardExpireMonthList', []))
    );
  }
}
